"use client";

import * as Sentry from "@sentry/nextjs";
import { useEffect } from "react";
import Link from "next/link";
import { Home, RefreshCw, AlertTriangle } from "lucide-react";

export default function GlobalError({
  error,
  reset,
}: {
  error: Error & { digest?: string };
  reset: () => void;
}) {
  useEffect(() => {
    Sentry.captureException(error);
    console.error(error);
  }, [error]);

  return (
    <html lang="en">
      <body className="font-sans antialiased">
        <div className="relative min-h-screen w-full overflow-hidden bg-linear-to-b from-gray-950 via-black to-gray-950">
          <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,rgba(212,168,75,0.03)_0%,transparent_50%)]" />

          <main className="relative z-10 min-h-screen flex items-center justify-center px-6">
            <div className="max-w-4xl mx-auto text-center">
              <div className="mb-8 inline-flex items-center justify-center w-20 h-20 rounded-full bg-red-500/10 border border-red-500/20">
                <AlertTriangle className="w-10 h-10 text-red-400" />
              </div>

              <h1 className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-bold text-white mb-6 tracking-tight">
                Something Went
                <br />
                <span className="text-[#D4A84B]">Unexpectedly Wrong</span>
              </h1>

              <p className="text-lg md:text-xl text-white/60 font-light max-w-2xl mx-auto mb-4">
                We apologize for the inconvenience. Our team has been notified
                and is working to resolve this issue.
              </p>

              {error.digest && (
                <p className="text-sm text-white/40 font-mono mb-12">
                  Error ID: {error.digest}
                </p>
              )}

              <div className="flex flex-col sm:flex-row items-center justify-center gap-4 mb-12">
                <button
                  onClick={reset}
                  className="group inline-flex items-center gap-3 px-8 py-4 text-base font-medium text-black bg-white rounded-full hover:bg-[#D4A84B] transition-all duration-300 hover:shadow-[0_0_30px_rgba(212,168,75,0.3)]"
                >
                  <RefreshCw className="w-5 h-5" />
                  Try Again
                </button>

                <Link
                  href="/"
                  className="group inline-flex items-center gap-3 px-8 py-4 text-base font-medium text-white bg-white/5 border border-white/10 rounded-full hover:bg-white/10 hover:border-[#D4A84B]/50 transition-all duration-300 hover:shadow-[0_0_30px_rgba(212,168,75,0.15)]"
                >
                  <Home className="w-5 h-5" />
                  Go Home
                </Link>
              </div>

              <details className="text-left max-w-2xl mx-auto">
                <summary className="text-sm text-white/60 hover:text-white/80 cursor-pointer mb-2">
                  Technical details
                </summary>
                <div className="mt-4 p-4 bg-black/40 backdrop-blur-sm border border-white/10 rounded-xl">
                  <p className="text-xs text-white/70 font-mono break-all">
                    {error.message || "Unknown error"}
                  </p>
                </div>
              </details>
            </div>

            <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,rgba(212,168,75,0.05)_0%,transparent_70%)] pointer-events-none" />
          </main>
        </div>
      </body>
    </html>
  );
}
